/**
 * Fetch wrapper for the TempleOS API (same origin through the Vite proxy). Sends the session cookie, the CSRF token on
 * writes and this terminal's device identity (SOW Modules 12 and 14). A NetworkError means "server unreachable" (the
 * POS keeps working offline); an ApiError is a real answer from the server.
 */
let csrfToken: string | null = null;
export const setCsrfToken = (t: string | null): void => { csrfToken = t; };

/** The device agent's pairing token and MAC (GET /identity), checked by the server for bound counters. */
export interface DeviceIdentity { token: string | null; mac: string | null }
let device: DeviceIdentity = { token: null, mac: null };
export const setDeviceIdentity = (d: DeviceIdentity): void => { device = d; };

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, message: string, readonly details?: unknown) {
    super(message);
  }
  fieldErrors(): Record<string, string> {
    const d = this.details as { fieldErrors?: Record<string, string[]>; field?: string } | undefined;
    if (d?.fieldErrors) return Object.fromEntries(Object.entries(d.fieldErrors).map(([k, v]) => [k, v[0] ?? "Invalid"]));
    if (d?.field) return { [d.field]: this.message };
    return {};
  }
}

export class NetworkError extends Error {
  constructor() {
    super("The temple server cannot be reached. Working offline.");
  }
}

export const isNetworkError = (e: unknown): boolean => e instanceof NetworkError;

let onUnauthorized: (() => void) | null = null;
export const setUnauthorizedHandler = (fn: (() => void) | null): void => { onUnauthorized = fn; };

/** Told after every request whether the server answered, so the header can show online / offline. */
let onReachability: ((reachable: boolean) => void) | null = null;
export const setReachabilityHandler = (fn: ((reachable: boolean) => void) | null): void => { onReachability = fn; };

const DEFAULT_TIMEOUT_MS = 8_000;

async function request<T>(method: string, url: string, body?: unknown, timeoutMs = DEFAULT_TIMEOUT_MS, retried = false): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (method !== "GET" && csrfToken) headers["x-csrf-token"] = csrfToken;
  if (device.token) headers["x-device-token"] = device.token;
  if (device.mac) headers["x-device-mac"] = device.mac;
  let res: Response;
  try {
    res = await fetch(url, { method, headers, credentials: "same-origin", body: body === undefined ? undefined : JSON.stringify(body), signal: AbortSignal.timeout(timeoutMs) });
  } catch {
    onReachability?.(false);
    throw new NetworkError();
  }
  const down = res.status >= 502 && res.status <= 504; // proxy up, API down
  onReachability?.(!down);
  if (down) throw new NetworkError();
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    throw new ApiError(res.status, "BAD_RESPONSE", `Unexpected reply from the server (${res.status}).`);
  }
  if (!res.ok) {
    const e = (data as { error?: { code: string; message: string; details?: unknown } } | null)?.error;
    const err = new ApiError(res.status, e?.code ?? "HTTP_ERROR", e?.message ?? `Request failed (${res.status})`, e?.details);
    // Only an ended session signs the cashier out. A supervisor's mistyped password (BAD_CREDENTIALS, TOTP_*) is a
    // 401 too, and must leave the cashier, the typed counts and the gate exactly where they were.
    if (res.status === 401 && err.code === "UNAUTHORIZED") onUnauthorized?.();
    // After an offline start or unlock the page has no CSRF token yet: fetch it once and resend.
    if (res.status === 403 && err.code === "CSRF" && !retried) {
      const me = await request<{ csrfToken?: string }>("GET", "/api/auth/me", undefined, timeoutMs, true);
      if (me.csrfToken) {
        csrfToken = me.csrfToken;
        return request<T>(method, url, body, timeoutMs, true);
      }
    }
    throw err;
  }
  return data as T;
}

export const api = {
  get: <T>(url: string, timeoutMs?: number) => request<T>("GET", url, undefined, timeoutMs),
  post: <T>(url: string, body: unknown = {}, timeoutMs?: number) => request<T>("POST", url, body, timeoutMs),
};

export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : "";
}

export const errorText = (e: unknown): string => (e instanceof Error ? e.message : "Something went wrong.");
